import {TSEigentumsverhaeltnis} from './enums/TSEigentumsverhaeltnis';
import {TSRechtsform} from './enums/TSRechtsform';
import {IHasUnconnectedDocuments} from './IHasUnconnectedDocuments';
import TSAbstractEntity from './TSAbstractEntity';
import TSFinanzangaben from './TSFinanzangaben';
import TSGenossenschaft from './TSGenossenschaft';
import TSGesuchsPerson from './TSGesuchsPerson';
import TSKoerperschaft from './TSKoerperschaft';
import {TSSerializable} from './TSSerializable';
import moment from 'moment';

export default class TSGesuchsteller extends TSAbstractEntity implements TSSerializable<TSGesuchsteller>, IHasUnconnectedDocuments {

    private _eigentumsverhaeltnis: TSEigentumsverhaeltnis;

    private _rechtsform: TSRechtsform;

    private _kontaktperson: TSGesuchsPerson | undefined;

    private _finanzangaben: TSFinanzangaben | undefined;

    private _genossenschaft: TSGenossenschaft | undefined;

    private _koerperschaft: TSKoerperschaft | undefined;

    // transient! kommt nicht vom Server und muss gesetzt werden beim laden
    private _isZweitperson: boolean | undefined;

    private _documentIdsToConnect: Array<string> = [];

    static readonly POST_2025_START_DATE: string = '2026-04-30';
    private static readonly MAX_ABZUGSFREI_PRE_2026: number = 1000000;
    private static readonly MAX_ABZUGSFREI_POST_2025: number = 1500000;
    static readonly MAX_VERMOEGEN_KAPITAL_PRE_2026: number = 2000000;
    static readonly MAX_VERMOEGEN_KAPITAL_POST_2025: number = 2500000;
    private static readonly VERMOEGENSABZUG_FACTOR: number = 0.05;
    private static readonly VERMOEGENSABZUG_ROUNDING_STEP: number = 50;

    constructor(isZweitperson: boolean = false) {
        super();
        if (isZweitperson) {
            this.isZweitperson = true
        } else {
            this.finanzangaben = new TSFinanzangaben();    // zweitperson hat keine finanzangaben
            this.isZweitperson = false;
        }
    }

    public get eigentumsverhaeltnis(): TSEigentumsverhaeltnis {
        return this._eigentumsverhaeltnis;
    }

    public set eigentumsverhaeltnis(value: TSEigentumsverhaeltnis) {
        this._eigentumsverhaeltnis = value;
    }

    public get rechtsform(): TSRechtsform {
        return this._rechtsform;
    }

    public set rechtsform(value: TSRechtsform) {
        this._rechtsform = value;
    }

    public get kontaktperson(): TSGesuchsPerson | undefined {
        return this._kontaktperson;
    }

    public set kontaktperson(value: TSGesuchsPerson | undefined) {
        this._kontaktperson = value;
    }

    public get finanzangaben(): TSFinanzangaben | undefined {
        return this._finanzangaben;
    }

    public set finanzangaben(value: TSFinanzangaben | undefined) {
        this._finanzangaben = value;
    }

    public get genossenschaft(): TSGenossenschaft | undefined {
        return this._genossenschaft;
    }

    public set genossenschaft(value: TSGenossenschaft | undefined) {
        this._genossenschaft = value;
    }

    public get koerperschaft(): TSKoerperschaft | undefined {
        return this._koerperschaft;
    }

    public set koerperschaft(value: TSKoerperschaft | undefined) {
        this._koerperschaft = value;
    }

    public get isZweitperson(): boolean | any {
        return this._isZweitperson;
    }

    public set isZweitperson(value: boolean | any) {
        this._isZweitperson = value;
    }

    public get documentIdsToConnect(): Array<string> {
        return this._documentIdsToConnect;
    }

    public set documentIdsToConnect(value: Array<string>) {
        this._documentIdsToConnect = value;
    }

    deserialize(restObject: any): TSGesuchsteller {
        super.deserialize(restObject);
        this.eigentumsverhaeltnis = restObject.eigentumsverhaeltnis;
        this.rechtsform = restObject.rechtsform;

        this.kontaktperson = this.deserializeOrCreate(restObject.kontaktperson, new TSGesuchsPerson());
        this.finanzangaben = this.deserializeOrCreate(restObject.finanzangaben, new TSFinanzangaben());
        this.genossenschaft = this.deserializeOrNull(restObject.genossenschaft, new TSGenossenschaft);
        this.koerperschaft = this.deserializeOrNull(restObject.koerperschaft, new TSKoerperschaft());

        return this;
    }

    serialize(restObject: any): any {
        super.serialize(restObject);
        this.cleanup(); // wird gebraucht, wenn ein Fall vom Server wieder zum Server geschickt wird
        restObject.eigentumsverhaeltnis = this.eigentumsverhaeltnis;
        restObject.rechtsform = this.rechtsform;
        restObject.kontaktperson = this.serializeOrInit(this.kontaktperson, restObject.kontaktperson);
        restObject.finanzangaben = this.serializeOrInit(this.finanzangaben, restObject.finanzangaben);
        restObject.genossenschaft = this.serializeOrInit(this.genossenschaft, restObject.genossenschaft);
        restObject.koerperschaft = this.serializeOrInit(this.koerperschaft, restObject.koerperschaft);

        if (this.isZweitperson) {
            restObject.finanzangaben = undefined;
        }
        return restObject;
    }

    private MRound(number: number, roundto: number) {
        return roundto * Math.round(number / roundto);
    }

    public getEinkommensabzug(): number {
        if (this.hasFinanzangaben() &&
            this.finanzangaben!.finanzangabenDatenFS.einkommenGewinn) {
            // Kein Abzub bis 100'000.- steuerbares Einkommen
            if (this.finanzangaben!.finanzangabenDatenFS.einkommenGewinn < 100000) {
                return 0;
            } else {
                // 20% des 100'000 übersteigenden Betrages (runden auf 200)
                return this.MRound(((this.finanzangaben!.finanzangabenDatenFS.einkommenGewinn - 100000) * 0.2), 200);
            }

        }
        return 0;
    }

    public getVermoegenssabzug(schadendatum: moment.Moment | undefined, ermittelterSchaden: number): number {
        if (!this.hasFinanzangaben()) {
            return 0;
        }

        const vermoegenKapital = this.finanzangaben!.finanzangabenDatenFS.vermoegenKapital;
        if (vermoegenKapital === undefined || vermoegenKapital === null) {
            return 0;
        }

        if (vermoegenKapital > TSGesuchsteller.MAX_VERMOEGEN_KAPITAL_POST_2025) {
            return ermittelterSchaden;
        }

        const isPost2025 = schadendatum === undefined  || schadendatum === null  ||
            schadendatum.isAfter(TSGesuchsteller.POST_2025_START_DATE, 'day');

        const maxAbzugsfrei = isPost2025
            ? TSGesuchsteller.MAX_ABZUGSFREI_POST_2025
            : TSGesuchsteller.MAX_ABZUGSFREI_PRE_2026;

        const deductionBase = vermoegenKapital - maxAbzugsfrei;

        if (deductionBase <= 0) {
            return 0;
        }

        return this.MRound(
            deductionBase * TSGesuchsteller.VERMOEGENSABZUG_FACTOR,
            TSGesuchsteller.VERMOEGENSABZUG_ROUNDING_STEP
        );
    }

    public hasFinanzangaben() {
        return this.finanzangaben && this.finanzangaben.finanzangabenDatenFS;
    }

    public cleanup(): void {
        switch (this.rechtsform) {
            case TSRechtsform.NATUERLICHE_PERSON:
                this.genossenschaft = undefined;
                this.koerperschaft = undefined;
                break;
            case TSRechtsform.GENOSSENSCHAFT:
                this.koerperschaft = undefined;
                if (this.kontaktperson) {
                    this.kontaktperson.gesuchsPersonDatenFS.adresse = undefined;
                }
                // finazangaben auf 0 setzen fuer berechnung wenn Genossenschaft nicht steuerpflichtig
                if (this.genossenschaft && !this.genossenschaft.genossenschaftDatenFS.isSteuerpflichtig) {
                    this.zeroOutFinanzangaben();
                }

                break;
            case TSRechtsform.MEHRPERSONENGESELLSCHAFT:
                this.koerperschaft = undefined;
                if (this.kontaktperson) {
                    this.kontaktperson.gesuchsPersonDatenFS.adresse = undefined;
                }

                if (this.isZweitperson) {
                    this.finanzangaben = undefined;
                } else {
                    // Mehrpersonengesellschaft hat kein einkommen und Gewinn. Muss für berechnung auf 0 gesetzt werden!
                    // Falls es aber eine Zweitperson ist gibt es hier keine finanzangaben
                    this.zeroOutFinanzangaben()
                }
                break;
            case TSRechtsform.ANDERE_KOERPERSCHAFT:
                this.genossenschaft = undefined;
                if (this.kontaktperson) {
                    this.kontaktperson.gesuchsPersonDatenFS.adresse = undefined;
                }
                break;
        }
    }

    private zeroOutFinanzangaben() {
        if (!this.finanzangaben || !this.finanzangaben.finanzangabenDatenFS) {
            console.error('error: finanzangaben bei einer erstperson mit rechtsfrom mehrpersonengesllschaft oder genossenschaft muessen gesetzt sein');
        } else {
            this.finanzangaben.finanzangabenDatenFS.einkommenGewinn = 0;
            this.finanzangaben.finanzangabenDatenFS.vermoegenKapital = 0;
            this.finanzangaben.steuerdatenGeprueft = true;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/models/TSGesuchsteller.ts